import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { 
    PlusIcon,
    PencilIcon,
    TrashIcon,
    EyeIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    Squares2X2Icon,
    PhotoIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

// Kategori Ekleme/Düzenleme Modalı
const CategoryFormModal = ({ category, parentCategories, closeModal, onSave }) => {
    const { token } = useAuth();
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: '',
        parent_id: '',
        image_url: '',
        icon: '',
        sort_order: 0,
        is_active: true
    });
    const [loading, setLoading] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);

    useEffect(() => {
        if (category) {
            setFormData({
                name: category.name || '',
                slug: category.slug || '',
                description: category.description || '',
                parent_id: category.parent_id || '',
                image_url: category.image_url || '',
                icon: category.icon || '',
                sort_order: category.sort_order || 0,
                is_active: category.is_active !== false
            });
            setImagePreview(category.image_url || '');
        }
    }, [category]);

    // Resim dosyası seçildiğinde
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            // Önizleme için URL oluştur
            const reader = new FileReader();
            reader.onload = (e) => setImagePreview(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    // Resmi yükle
    const uploadImage = async () => {
        if (!imageFile) return null;

        setUploadingImage(true);
        try {
            const formData = new FormData();
            formData.append('categoryImage', imageFile);

            const response = await api.post('/api/categories/upload-image', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });

            console.log('Upload response:', response); // Debug log
            
            // Response'u kontrol et
            const responseData = response.data || response;
            console.log('Response data:', responseData); // Debug log
            
            if (responseData && responseData.success) {
                return responseData.imageUrl;
            } else if (responseData && responseData.imageUrl) {
                // Bazen success field olmayabilir ama imageUrl varsa
                return responseData.imageUrl;
            } else {
                throw new Error(responseData?.error || 'Resim yükleme başarısız');
            }
        } catch (error) {
            console.error('Resim yükleme hatası:', error);
            console.error('Error response:', error.response); // Debug log
            toast.error(error.response?.data?.error || 'Resim yüklenirken hata oluştu');
            return null;
        } finally {
            setUploadingImage(false);
        }
    };

    const generateSlug = (name) => {
        return name
            .toLowerCase()
            .replace(/ğ/g, 'g')
            .replace(/ü/g, 'u')
            .replace(/ş/g, 's')
            .replace(/ı/g, 'i')
            .replace(/ö/g, 'o')
            .replace(/ç/g, 'c')
            .replace(/[^a-z0-9]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };

    const handleNameChange = (e) => {
        const name = e.target.value;
        setFormData(prev => ({
            ...prev,
            name,
            slug: generateSlug(name)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Önce resim yükle (eğer yeni resim seçildiyse)
            let imageUrl = formData.image_url;
            if (imageFile) {
                const uploadedImageUrl = await uploadImage();
                if (uploadedImageUrl) {
                    imageUrl = uploadedImageUrl;
                }
            }

            // parent_id boş string ise null yap
            const cleanFormData = {
                ...formData,
                image_url: imageUrl,
                parent_id: formData.parent_id === '' ? null : parseInt(formData.parent_id)
            };

            if (category) {
                // Güncelleme
                await api.put(`/api/categories/${category.id}`, cleanFormData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Kategori başarıyla güncellendi');
            } else {
                // Yeni ekleme
                await api.post('/api/categories', cleanFormData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Kategori başarıyla eklendi');
            }
            onSave();
            closeModal();
        } catch (error) {
            console.error('Kategori kaydetme hatası:', error);
            toast.error(error.response?.data?.error || 'Kategori kaydedilirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const iconOptions = [
        { value: 'fas fa-baby', label: '👶 Bebek' },
        { value: 'fas fa-child', label: '🧒 Çocuk' },
        { value: 'fas fa-tshirt', label: '👕 Giyim' },
        { value: 'fas fa-shoe-prints', label: '👟 Ayakkabı' },
        { value: 'fas fa-hat-cowboy', label: '🎩 Aksesuar' },
        { value: 'fas fa-female', label: '👩 Kız' },
        { value: 'fas fa-male', label: '👨 Erkek' },
        { value: 'fas fa-baby-carriage', label: '🍼 Bakım' },
        { value: 'fas fa-heart', label: '❤️ Favori' },
        { value: 'fas fa-star', label: '⭐ Özel' }
    ];

    return (
        <div className="p-6 max-w-2xl w-full bg-white rounded-lg">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">
                {category ? 'Kategori Düzenle' : 'Yeni Kategori Ekle'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Kategori Adı */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Kategori Adı *
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={handleNameChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-brand-green focus:border-brand-green"
                            required
                        />
                    </div>

                    {/* Slug */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            URL Slug *
                        </label>
                        <input
                            type="text"
                            value={formData.slug}
                            onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-brand-green focus:border-brand-green"
                            required
                        />
                    </div>
                </div>

                {/* Açıklama */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Açıklama
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-brand-green focus:border-brand-green"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Üst Kategori */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Üst Kategori
                        </label>
                        <select
                            value={formData.parent_id}
                            onChange={(e) => setFormData(prev => ({ ...prev, parent_id: e.target.value || null }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-brand-green focus:border-brand-green font-mono text-sm"
                        >
                            <option value="">🏠 Ana Kategori (En Üst Seviye)</option>
                            {parentCategories.map(cat => (
                                <option key={cat.id} value={cat.id} className="font-mono">
                                    {cat.displayName || cat.name}
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            {formData.parent_id ? 
                                '📁 Bu kategori seçilen kategorinin alt kategorisi olacak' : 
                                '🏠 Bu kategori ana kategori olacak (en üst seviye)'
                            }
                        </p>
                        
                        {/* Hiyerarşi Önizlemesi */}
                        {formData.parent_id && (
                            <div className="mt-2 p-2 bg-blue-50 rounded border">
                                <p className="text-xs font-medium text-blue-700 mb-1">Kategori Hiyerarşisi:</p>
                                <div className="text-xs text-blue-600 font-mono">
                                    {(() => {
                                        const selectedParent = parentCategories.find(p => p.id == formData.parent_id);
                                        if (selectedParent) {
                                            const parentPath = selectedParent.displayName || selectedParent.name;
                                            const currentLevel = selectedParent.level + 1;
                                            const indent = currentLevel === 1 ? '├─ ' : 
                                                          currentLevel === 2 ? '│  ├─ ' : 
                                                          currentLevel === 3 ? '│  │  ├─ ' : 
                                                          '│  '.repeat(currentLevel - 1) + '├─ ';
                                            return (
                                                <>
                                                    <div>{parentPath}</div>
                                                    <div className="text-green-600 font-bold">
                                                        {indent}{formData.name || 'Yeni Kategori'}
                                                    </div>
                                                </>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sıra */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Sıra
                        </label>
                        <input
                            type="number"
                            value={formData.sort_order}
                            onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-brand-green focus:border-brand-green"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* İkon */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            İkon
                        </label>
                        <select
                            value={formData.icon}
                            onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-brand-green focus:border-brand-green"
                        >
                            <option value="">İkon Seçin</option>
                            {iconOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {formData.icon && (
                            <div className="mt-2 flex items-center">
                                <i className={`${formData.icon} text-brand-green mr-2`}></i>
                                <span className="text-sm text-gray-600">Önizleme</span>
                            </div>
                        )}
                    </div>

                    {/* Resim URL */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Resim URL (Manuel)
                        </label>
                        <input
                            type="url"
                            value={formData.image_url}
                            onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-brand-green focus:border-brand-green"
                            placeholder="https://example.com/image.jpg"
                        />
                    </div>
                </div>

                {/* Resim Yükleme */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Kategori Resmi Yükle
                    </label>
                    <div className="flex items-center space-x-4">
                        <div className="flex-1">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-brand-green focus:border-brand-green"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                JPG, PNG, GIF formatları desteklenir. Maksimum 10MB.
                            </p>
                        </div>
                        {imagePreview && (
                            <div className="w-20 h-20 border border-gray-300 rounded-lg overflow-hidden">
                                <img
                                    src={imagePreview}
                                    alt="Önizleme"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Aktif Durumu */}
                <div>
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                            className="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Kategori aktif</span>
                    </label>
                </div>

                {/* Butonlar */}
                <div className="flex justify-end space-x-4 pt-6 border-t">
                    <button
                        type="button"
                        onClick={closeModal}
                        className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                    >
                        İptal
                    </button>
                    <button
                        type="submit"
                        disabled={loading || uploadingImage}
                        className="px-6 py-2 bg-brand-green text-white rounded-lg hover:bg-brand-green-dark disabled:opacity-50"
                    >
                        {uploadingImage ? 'Resim Yükleniyor...' : 
                         loading ? 'Kaydediliyor...' : 
                         (category ? 'Güncelle' : 'Ekle')}
                    </button>
                </div>
            </form>
        </div>
    );
};

// Kategori Detay Modalı
const CategoryDetailModal = ({ category, closeModal, onEdit, onDelete }) => {
    if (!category) return null;

    return (
        <div className="p-6 max-w-2xl w-full bg-white rounded-lg">
            <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Kategori Detayları</h3>
                <div className="flex space-x-2">
                    <button
                        onClick={() => onEdit(category)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Düzenle"
                    >
                        <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                        onClick={() => onDelete(category)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Sil"
                    >
                        <TrashIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>

            <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Kategori Adı</label>
                        <p className="text-lg font-semibold text-gray-900">{category.name}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">URL Slug</label>
                        <p className="text-gray-900 font-mono">{category.slug}</p>
                    </div>
                </div>

                {category.description && (
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Açıklama</label>
                        <p className="text-gray-900">{category.description}</p>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Üst Kategori</label>
                        <p className="text-gray-900">{category.parent?.name || 'Ana Kategori'}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Sıra</label>
                        <p className="text-gray-900">{category.sort_order}</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Durum</label>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            category.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                        }`}>
                            {category.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                    </div>
                </div>

                {(category.icon || category.image_url) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {category.icon && (
                            <div>
                                <label className="block text-sm font-medium text-gray-500">İkon</label>
                                <div className="flex items-center">
                                    <i className={`${category.icon} text-2xl text-brand-green mr-2`}></i>
                                    <span className="text-gray-600 text-sm">{category.icon}</span>
                                </div>
                            </div>
                        )}
                        {category.image_url && (
                            <div>
                                <label className="block text-sm font-medium text-gray-500">Resim</label>
                                <img 
                                    src={category.image_url} 
                                    alt={category.name}
                                    className="w-20 h-20 object-cover rounded-lg"
                                />
                            </div>
                        )}
                    </div>
                )}

                {category.children && category.children.length > 0 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-2">Alt Kategoriler</label>
                        <div className="flex flex-wrap gap-2">
                            {category.children.map(child => (
                                <span 
                                    key={child.id}
                                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                                >
                                    {child.name}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-500">Ürün Sayısı</label>
                    <p className="text-lg font-semibold text-brand-green">{category.product_count || 0} ürün</p>
                </div>
            </div>

            <div className="flex justify-end pt-6 border-t mt-6">
                <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                    Kapat
                </button>
            </div>
        </div>
    );
};

function CategoryManagementPanel({ openModal, closeModal }) {
    const { token } = useAuth();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedCategories, setExpandedCategories] = useState(new Set());

    // Düz listeyi hiyerarşik yapıya dönüştür
    const buildHierarchy = (flatList) => {
        const categoryMap = new Map();
        const rootCategories = [];
        
        // Önce tüm kategorileri map'e ekle
        flatList.forEach(cat => {
            categoryMap.set(cat.id, {
                ...cat,
                children: []
            });
        });
        
        // Sonra hiyerarşiyi oluştur
        flatList.forEach(cat => {
            if (cat.parent_id === null) {
                rootCategories.push(categoryMap.get(cat.id));
            } else {
                const parent = categoryMap.get(cat.parent_id);
                if (parent) {
                    parent.children.push(categoryMap.get(cat.id));
                }
            }
        });
        
        return rootCategories;
    };

    const fetchCategories = useCallback(async () => {
        if (!token) return;

        setLoading(true);
        try {
            const response = await api.get(`/api/categories?include_inactive=true`);
            const flatCategories = response.data || [];
            const hierarchicalCategories = buildHierarchy(flatCategories);
            setCategories(hierarchicalCategories);
        } catch (error) {
            console.error('Kategoriler yüklenirken hata:', error);
            toast.error('Kategoriler yüklenemedi');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const toggleExpanded = (categoryId) => {
        const newExpanded = new Set(expandedCategories);
        if (newExpanded.has(categoryId)) {
            newExpanded.delete(categoryId);
        } else {
            newExpanded.add(categoryId);
        }
        setExpandedCategories(newExpanded);
    };

    const handleAdd = () => {
        const availableParents = flattenCategoriesForParentSelection(categories);
        
        openModal(
            <CategoryFormModal
                parentCategories={availableParents}
                closeModal={closeModal}
                onSave={() => {
                    fetchCategories();
                    setTimeout(() => fetchCategories(), 1000); // 1 saniye sonra tekrar yükle
                }}
            />
        );
    };

    // Kategorileri düzleştir ve hiyerarşik gösterim için hazırla
    const flattenCategoriesForParentSelection = (categories, currentCategoryId = null, level = 0) => {
        let flattened = [];
        
        categories.forEach(category => {
            // Kendisini ve tüm alt kategorilerini (descendants) hariç tut
            const isCurrentOrDescendant = (cat, targetId) => {
                if (cat.id === targetId) return true;
                if (cat.children && cat.children.length > 0) {
                    return cat.children.some(child => isCurrentOrDescendant(child, targetId));
                }
                return false;
            };
            
            if (!isCurrentOrDescendant(category, currentCategoryId)) {
                // Seviye gösterimi için Unicode karakterler kullan
                const indent = level === 0 ? '' : 
                              level === 1 ? '├─ ' : 
                              level === 2 ? '│  ├─ ' : 
                              level === 3 ? '│  │  ├─ ' : 
                              '│  '.repeat(level - 1) + '├─ ';
                
                flattened.push({
                    ...category,
                    level,
                    displayName: indent + category.name,
                    canBeParent: true
                });
                
                // Alt kategorileri de ekle
                if (category.children && category.children.length > 0) {
                    const childrenFlattened = flattenCategoriesForParentSelection(
                        category.children, 
                        currentCategoryId, 
                        level + 1
                    );
                    flattened = flattened.concat(childrenFlattened);
                }
            }
        });
        
        return flattened;
    };

    const handleEdit = (category) => {
        const availableParents = flattenCategoriesForParentSelection(categories, category.id);
        
        openModal(
            <CategoryFormModal
                category={category}
                parentCategories={availableParents}
                closeModal={closeModal}
                onSave={fetchCategories}
            />
        );
    };

    const handleView = (category) => {
        openModal(
            <CategoryDetailModal
                category={category}
                closeModal={closeModal}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />
        );
    };

    const handleDelete = async (category) => {
        if (!window.confirm(`"${category.name}" kategorisini silmek istediğinizden emin misiniz?`)) {
            return;
        }

        try {
            await api.delete(`/api/categories/${category.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Kategori başarıyla silindi');
            fetchCategories();
            closeModal();
        } catch (error) {
            console.error('Kategori silme hatası:', error);
            toast.error(error.response?.data?.error || 'Kategori silinirken bir hata oluştu');
        }
    };

    const renderCategory = (category, level = 0) => {
        const hasChildren = category.children && category.children.length > 0;
        const isExpanded = expandedCategories.has(category.id);

        return (
            <div key={category.id}>
                <div 
                    className={`flex items-center justify-between p-3 border-b border-gray-100 hover:bg-gray-50 ${
                        level > 0 ? 'ml-' + (level * 6) : ''
                    }`}
                >
                    <div className="flex items-center flex-1">
                        {hasChildren && (
                            <button
                                onClick={() => toggleExpanded(category.id)}
                                className="p-1 mr-2 hover:bg-gray-200 rounded"
                            >
                                {isExpanded ? (
                                    <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                                ) : (
                                    <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                                )}
                            </button>
                        )}
                        
                        {!hasChildren && level > 0 && (
                            <div className="w-6 mr-2"></div>
                        )}

                        {category.icon && (
                            <i className={`${category.icon} text-brand-green mr-3`}></i>
                        )}

                        <div className="flex-1">
                            <div className="flex items-center">
                                <h3 className="font-medium text-gray-900">{category.name}</h3>
                                <span className="ml-2 text-xs text-gray-500">({category.product_count || 0})</span>
                                {category.is_active === false && (
                                    <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                                        Pasif
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-gray-500">{category.slug}</p>
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => handleView(category)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Detayları Görüntüle"
                        >
                            <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => handleEdit(category)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Düzenle"
                        >
                            <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => handleDelete(category)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Sil"
                        >
                            <TrashIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {hasChildren && isExpanded && (
                    <div>
                        {category.children.map(child => renderCategory(child, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="container mx-auto p-2 sm:p-4">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-brand-green to-brand-green-light p-4 sm:p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">Kategori Yönetimi</h2>
                            <p className="opacity-90 text-sm sm:text-base">Kategorileri yönetin ve düzenleyin</p>
                        </div>
                        <Squares2X2Icon className="h-8 w-8 opacity-80" />
                    </div>
                </div>

                <div className="p-4 sm:p-6">
                    {/* Üst Kontroller */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={handleAdd}
                                className="inline-flex items-center px-4 py-2 bg-brand-green text-white rounded-lg hover:bg-brand-green-dark transition-colors"
                            >
                                <PlusIcon className="h-5 w-5 mr-2" />
                                Yeni Kategori
                            </button>
                            <button
                                onClick={fetchCategories}
                                className="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                <ArrowPathIcon className="h-5 w-5 mr-2" />
                                Yenile
                            </button>
                        </div>
                        
                        <div className="text-sm text-gray-600">
                            Toplam {categories.length} kategori
                        </div>
                    </div>

                    {/* Kategoriler Listesi */}
                    {loading ? (
                        <div className="space-y-4">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="animate-pulse">
                                    <div className="h-16 bg-gray-200 rounded-lg"></div>
                                </div>
                            ))}
                        </div>
                    ) : categories.length === 0 ? (
                        <div className="text-center py-12">
                            <Squares2X2Icon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz kategori yok</h3>
                            <p className="text-gray-500 mb-4">İlk kategorinizi oluşturmak için yukarıdaki butonu kullanın</p>
                            <button
                                onClick={handleAdd}
                                className="inline-flex items-center px-4 py-2 bg-brand-green text-white rounded-lg hover:bg-brand-green-dark"
                            >
                                <PlusIcon className="h-5 w-5 mr-2" />
                                İlk Kategoriyi Ekle
                            </button>
                        </div>
                    ) : (
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            {categories.map(category => renderCategory(category))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CategoryManagementPanel;